<template>
  <div>
    <!-- Header -->
    <b-card class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-1">Reporte de Carga de Catedráticos</h3>
          <p class="text-muted mb-0">Visualiza la carga académica de los catedráticos por plan</p>
        </div>
        <b-button
          variant="outline-success"
          @click="exportToPDF"
          :disabled="!reporteData || loading"
          v-b-tooltip.hover
          title="Exportar a PDF"
        >
          <feather-icon icon="DownloadIcon" class="mr-1" />
          Exportar PDF
        </b-button>
      </div>
    </b-card>

    <!-- Filtros -->
    <b-card class="mb-4">
      <h6 class="mb-3">Filtros</h6>
      <b-row>
        <!-- Tipo de Plan -->
        <b-col md="4">
          <b-form-group label="Tipo de Plan *" label-for="tipoPlan">
            <b-form-select
              id="tipoPlan"
              v-model="filters.tipoPlanId"
              :options="tipoPlanOptions"
              @change="onTipoPlanChange"
              placeholder="Seleccionar plan"
              :disabled="loading"
            />
          </b-form-group>
        </b-col>

        <!-- Programación Académica (Opcional) -->
        <b-col md="4">
          <b-form-group label="Programación Académica (Opcional)" label-for="programacion">
            <b-form-select
              id="programacion"
              v-model="filters.programacionAcademicaId"
              :options="programacionesOptions"
              placeholder="Todas"
              :disabled="loading || !filters.tipoPlanId"
            />
          </b-form-group>
        </b-col>

        <!-- Botón de Búsqueda -->
        <b-col md="4" class="d-flex align-items-end">
          <b-button
            variant="primary"
            @click="loadReporte"
            :disabled="!filters.tipoPlanId || loading"
            class="w-100"
          >
            <feather-icon icon="SearchIcon" class="mr-1" />
            {{ loading ? 'Cargando...' : 'Generar Reporte' }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2">Cargando reporte...</p>
    </div>

    <!-- Error State -->
    <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = null">
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertTriangleIcon" size="20" class="mr-2 mt-1" />
        <div>
          <h6 class="alert-heading mb-1">Error al cargar el reporte</h6>
          <p class="mb-0">{{ error }}</p>
        </div>
      </div>
    </b-alert>

    <!-- Reporte -->
    <b-card v-if="reporteData && !loading" class="table-card">
      <!-- Información del Reporte -->
      <div class="mb-3">
        <h5 class="mb-2">{{ reporteData.tipoPlanNombre }}</h5>
        <div v-if="reporteData.periodoCodigo || reporteData.carreraNombre">
          <b-badge variant="info" class="mr-2" v-if="reporteData.periodoCodigo">
            {{ reporteData.periodoCodigo }}
          </b-badge>
          <b-badge variant="primary" v-if="reporteData.carreraNombre">
            {{ reporteData.carreraNombre }}
          </b-badge>
        </div>
      </div>

      <!-- Estadísticas -->
      <b-row class="mb-3" v-if="estadisticas">
        <b-col md="3">
          <div class="stats-card">
            <div class="stats-value">{{ estadisticas.totalCatedraticosConCarga }}</div>
            <div class="stats-label">Catedráticos Activos</div>
          </div>
        </b-col>
        <b-col md="3">
          <div class="stats-card">
            <div class="stats-value">{{ estadisticas.cargaPromedio.toFixed(1) }}</div>
            <div class="stats-label">Promedio de Cursos</div>
          </div>
        </b-col>
        <b-col md="3">
          <div class="stats-card">
            <div class="stats-value">{{ estadisticas.cargaMaxima }}</div>
            <div class="stats-label">Carga Máxima</div>
          </div>
        </b-col>
        <b-col md="3">
          <div class="stats-card">
            <div class="stats-value">{{ estadisticas.cargaMinima }}</div>
            <div class="stats-label">Carga Mínima</div>
          </div>
        </b-col>
      </b-row>

      <!-- Tabla de Carga -->
      <div class="table-responsive mt-3">
        <table class="table-carga-catedraticos">
          <thead>
            <tr>
              <th class="fixed-column header-no" rowspan="2">No.</th>
              <th class="fixed-column header-catedratico" rowspan="2">Catedrático</th>
              <th class="fixed-column header-codigo" rowspan="2">Código</th>
              <th 
                v-for="horario in reporteData.horarios" 
                :key="horario.horarioId"
                class="header-horario"
              >
                {{ horario.codigo }}
              </th>
              <th class="header-total" rowspan="2">Total cursos asignados</th>
            </tr>
            <tr>
              <th 
                v-for="horario in reporteData.horarios" 
                :key="`sub-${horario.horarioId}`"
                class="sub-header"
              >
                {{ horario.diaSemana }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(catedratico, index) in reporteData.catedraticos" 
              :key="catedratico.catedraticoId"
            >
              <td class="fixed-column cell-no">{{ index + 1 }}</td>
              <td class="fixed-column cell-catedratico">{{ catedratico.catedraticoNombre }}</td>
              <td class="fixed-column cell-codigo">{{ catedratico.catedraticoCodigo }}</td>
              <td 
                v-for="horario in reporteData.horarios" 
                :key="`asig-${catedratico.catedraticoId}-${horario.horarioId}`"
                class="cell-asignacion"
                :class="{ 'has-assignment': getAsignacion(catedratico, horario.horarioId) }"
                v-b-tooltip.hover
                :title="getAsignacionTooltip(catedratico, horario.horarioId)"
              >
                <div v-if="getAsignacion(catedratico, horario.horarioId)">
                  <strong>{{ getAsignacion(catedratico, horario.horarioId).cursoNombre }}</strong>
                  <br>
                  <small>{{ getAsignacion(catedratico, horario.horarioId).seccionCodigo }}</small>
                  <br>
                  <small class="text-muted">
                    {{ getAsignacion(catedratico, horario.horarioId).edificioNombre }} - 
                    {{ getAsignacion(catedratico, horario.horarioId).salonNumero }}
                  </small>
                </div>
                <span v-else class="empty-cell">-</span>
              </td>
              <td class="cell-total">
                <b-badge variant="primary" pill>
                  {{ catedratico.totalCursosAsignados }}
                </b-badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="reporteData.catedraticos.length === 0" class="text-center py-5">
        <feather-icon icon="InboxIcon" size="48" class="mb-3 text-muted" />
        <h6 class="text-muted">No hay datos para mostrar</h6>
        <p class="text-muted mb-0">No se encontraron asignaciones para el plan seleccionado</p>
      </div>
    </b-card>

    <!-- Empty State Inicial -->
    <b-card v-if="!reporteData && !loading && !error" class="text-center py-5">
      <feather-icon icon="PieChartIcon" size="64" class="mb-3 text-muted" />
      <h5 class="mb-2">Reporte de Carga de Catedráticos</h5>
      <p class="text-muted mb-0">
        Selecciona un tipo de plan y genera el reporte para visualizar la carga académica
      </p>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BSpinner,
  BAlert, BBadge,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import cargaCatedraticoService from '@/services/cargaCatedraticoService'
import tipoPlanService from '@/services/tipoPlanService'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reporteData: null,
      estadisticas: null,
      tipoPlanes: [],
      programaciones: [],
      
      filters: {
        tipoPlanId: null,
        programacionAcademicaId: null,
      },
    }
  },
  computed: {
    tipoPlanOptions() {
      return [
        { value: null, text: 'Seleccionar tipo de plan' },
        ...this.tipoPlanes.map(tp => ({
          value: tp.tipoPlanId,
          text: tp.nombre,
        }))
      ]
    },
    programacionesOptions() {
      return [
        { value: null, text: 'Todas las programaciones' },
        ...this.programaciones.map(p => ({
          value: p.programacionAcademicaId,
          text: `${p.periodoCodigo} - ${p.carreraNombre}`,
        }))
      ]
    },
  },
  async mounted() {
    await this.loadTipoPlanes()
  },
  methods: {
    async loadTipoPlanes() {
      try {
        this.tipoPlanes = await tipoPlanService.getAll()
      } catch (error) {
        console.error('Error loading tipo planes:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar los tipos de plan.',
          },
        })
      }
    },

    async onTipoPlanChange() {
      this.filters.programacionAcademicaId = null
      this.programaciones = []
      this.reporteData = null
      this.estadisticas = null

      if (this.filters.tipoPlanId) {
        try {
          this.programaciones = await cargaCatedraticoService.getProgramacionesByTipoPlan(
            this.filters.tipoPlanId
          )
        } catch (error) {
          console.error('Error loading programaciones:', error)
        }
      }
    },

    async loadReporte() {
      if (!this.filters.tipoPlanId) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Advertencia',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'Debe seleccionar un tipo de plan.',
          },
        })
        return
      }

      this.loading = true
      this.error = null

      try {
        // Cargar reporte
        this.reporteData = await cargaCatedraticoService.getReporteCarga(
          this.filters.tipoPlanId,
          this.filters.programacionAcademicaId
        )

        // Cargar estadísticas
        this.estadisticas = await cargaCatedraticoService.getEstadisticasCarga(
          this.filters.tipoPlanId,
          this.filters.programacionAcademicaId
        )

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Reporte Generado',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'El reporte se ha generado correctamente.',
          },
        })
      } catch (error) {
        console.error('Error loading reporte:', error)
        this.error = error.response?.data?.message || 'Error al cargar el reporte. Intente nuevamente.'
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: this.error,
          },
        })
      } finally {
        this.loading = false
      }
    },

    getAsignacion(catedratico, horarioId) {
      return catedratico.asignaciones.find(a => a.horarioId === horarioId && a.tieneAsignacion)
    },

    getAsignacionTooltip(catedratico, horarioId) {
      const asignacion = this.getAsignacion(catedratico, horarioId)
      if (!asignacion) return 'Sin asignación'
      
      return `${asignacion.cursoNombre} - ${asignacion.seccionCodigo}\n${asignacion.edificioNombre} - Salón ${asignacion.salonNumero}`
    },

    async exportToPDF() {
      if (!this.reporteData) return

      try {
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        })
        
        // Título
        doc.setFontSize(16)
        doc.text('Reporte de Carga de Catedráticos', 14, 15)
        
        // Subtítulo
        doc.setFontSize(12)
        doc.text(this.reporteData.tipoPlanNombre, 14, 22)
        
        if (this.reporteData.periodoCodigo || this.reporteData.carreraNombre) {
          const subtitle = [
            this.reporteData.periodoCodigo,
            this.reporteData.carreraNombre
          ].filter(Boolean).join(' - ')
          doc.setFontSize(10)
          doc.text(subtitle, 14, 28)
        }

        // Preparar datos para la tabla
        const headers = [
          'No.',
          'Catedrático',
          'Código',
          ...this.reporteData.horarios.map(h => h.codigo),
          'Total'
        ]

        const rows = this.reporteData.catedraticos.map((cat, index) => [
          index + 1,
          cat.catedraticoNombre,
          cat.catedraticoCodigo,
          ...this.reporteData.horarios.map(h => {
            const asig = this.getAsignacion(cat, h.horarioId)
            return asig ? `${asig.cursoNombre}\n${asig.seccionCodigo}` : '-'
          }),
          cat.totalCursosAsignados
        ])

        doc.autoTable({
          head: [headers],
          body: rows,
          startY: 35,
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [66, 139, 202], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 50 },
            2: { cellWidth: 20 },
          },
        })

        doc.save(`Carga_Catedraticos_${this.reporteData.tipoPlanNombre}_${new Date().getTime()}.pdf`)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'PDF Exportado',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'El reporte PDF se ha descargado correctamente.',
          },
        })
      } catch (error) {
        console.error('Error exporting PDF:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al exportar el PDF. Intente nuevamente.',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.table-card {
  overflow-x: auto;
}

.table-responsive {
  overflow-x: auto;
  max-width: 100%;
}

.table-carga-catedraticos {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  min-width: 1200px;
}

.table-carga-catedraticos th,
.table-carga-catedraticos td {
  border: 1px solid #dee2e6;
  padding: 8px;
  text-align: center;
  vertical-align: middle;
}

.table-carga-catedraticos thead th {
  background-color: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.fixed-column {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 5;
}

.header-no {
  width: 50px;
  left: 0;
  z-index: 11;
}

.header-catedratico {
  width: 250px;
  left: 50px;
  z-index: 11;
}

.header-codigo {
  width: 100px;
  left: 300px;
  z-index: 11;
}

.cell-no {
  left: 0;
}

.cell-catedratico {
  left: 50px;
  text-align: left;
}

.cell-codigo {
  left: 300px;
}

.header-horario {
  min-width: 120px;
  background-color: #e7f3ff;
}

.sub-header {
  font-size: 0.75rem;
  color: #6c757d;
  background-color: #f0f8ff;
}

.header-total {
  background-color: #fff3cd;
  min-width: 100px;
}

.cell-asignacion {
  min-width: 120px;
  font-size: 0.8rem;
  padding: 4px;
}

.cell-asignacion.has-assignment {
  background-color: #d4edda;
}

.cell-asignacion strong {
  display: block;
  margin-bottom: 2px;
}

.cell-asignacion small {
  display: block;
  line-height: 1.2;
}

.empty-cell {
  color: #dee2e6;
  font-size: 1.2rem;
}

.cell-total {
  background-color: #fff3cd;
  font-weight: 600;
}

.stats-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  padding: 20px;
  color: white;
  text-align: center;
  margin-bottom: 10px;
}

.stats-value {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
}

.stats-label {
  font-size: 0.9rem;
  opacity: 0.9;
}
</style>

